import React, { useMemo, useState } from "react";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowRightLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * MergeBankAccountsDialog — pick a destination account, then tick the duplicates
 * to merge into it. All accounts must share the same currency. Calls
 * POST /api/bank-accounts/merge.
 *
 * Props:
 *   open / onOpenChange     — control dialog visibility
 *   accounts                — full list of bank accounts
 *   onSelectCanonical(id)   — fired after merge so the parent can switch selection
 *   onMerged()              — fired after successful merge so the parent can reload
 */
export default function MergeBankAccountsDialog({ open, onOpenChange, accounts, onSelectCanonical, onMerged }) {
    const [canonical, setCanonical] = useState(null);
    const [dups, setDups] = useState(new Set());
    const [busy, setBusy] = useState(false);

    const canonicalAcct = useMemo(
        () => accounts.find((a) => a.id === canonical) || null,
        [accounts, canonical],
    );

    const conflicts = useMemo(() => {
        if (!canonicalAcct) return [];
        return Array.from(dups).filter((id) => {
            const a = accounts.find((x) => x.id === id);
            return a && a.currency !== canonicalAcct.currency;
        });
    }, [dups, canonicalAcct, accounts]);

    const toggle = (id) => {
        setDups((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const reset = () => { setCanonical(null); setDups(new Set()); };

    const close = (v) => {
        if (busy) return;
        if (!v) reset();
        onOpenChange?.(v);
    };

    const run = async () => {
        if (!canonicalAcct || dups.size === 0) return;
        if (conflicts.length) {
            toast.error(`All accounts must share the same currency as the destination (${canonicalAcct.currency}).`);
            return;
        }
        const names = Array.from(dups).map((id) => accounts.find((a) => a.id === id)?.name).filter(Boolean);
        if (!window.confirm(`Merge ${names.length} account${names.length === 1 ? "" : "s"} into "${canonicalAcct.name}"?\n\n${names.join(", ")}\n\nAll payments, expenses, and imported transactions will move to "${canonicalAcct.name}" and the duplicate accounts will be deleted. This cannot be undone.`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/bank-accounts/merge", {
                canonical_id: canonical,
                duplicate_ids: Array.from(dups),
            });
            const parts = [];
            parts.push(`${data.duplicates_removed} account${data.duplicates_removed === 1 ? "" : "s"} merged`);
            const moved = (data.payments_moved || 0) + (data.bill_payments_moved || 0) + (data.expenses_moved || 0) + (data.bank_txns_moved || 0);
            if (moved) parts.push(`${moved} record${moved === 1 ? "" : "s"} moved`);
            toast.success(parts.join(" · "));
            onSelectCanonical?.(canonical);
            onMerged?.();
            reset();
            onOpenChange?.(false);
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (d && typeof d === "object" && d.message) toast.error(d.message);
            else toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-secondary" /> Merge bank accounts
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                        Pick the account to <strong>keep</strong>, then tick the duplicate accounts to merge into it. Every payment, expense, and imported transaction will move over, balances will roll up, and the duplicate accounts will be deleted. All accounts must share the same currency.
                    </p>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Keep this account</Label>
                        <Select value={canonical || ""} onValueChange={(v) => { setCanonical(v); setDups(new Set()); }}>
                            <SelectTrigger className="mt-1.5" data-testid="merge-canonical-select">
                                <SelectValue placeholder="Select the destination account" />
                            </SelectTrigger>
                            <SelectContent>
                                {accounts.map((a) => (
                                    <SelectItem key={a.id} value={a.id}>
                                        {a.name} · {a.currency} · {fmtMoney(a.balance, a.currency)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {canonicalAcct && (
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Merge into it</Label>
                            <div className="mt-1.5 rounded-2xl border border-sand-300 divide-y divide-sand-100 overflow-hidden">
                                {accounts.filter((a) => a.id !== canonical).map((a) => {
                                    const conflict = a.currency !== canonicalAcct.currency;
                                    const checked = dups.has(a.id);
                                    return (
                                        <label
                                            key={a.id}
                                            className={`flex items-center justify-between gap-3 px-4 py-3 cursor-pointer ${conflict ? "bg-coral/5 text-muted-foreground" : checked ? "bg-secondary/5" : "hover:bg-sand-50"}`}
                                            data-testid={`merge-dup-row-${a.id}`}
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={conflict || busy}
                                                    onChange={() => toggle(a.id)}
                                                    className="h-4 w-4 accent-secondary cursor-pointer disabled:cursor-not-allowed"
                                                    data-testid={`merge-dup-toggle-${a.id}`}
                                                />
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium text-ocean-700 truncate">{a.name}</div>
                                                    <div className="text-xs text-muted-foreground truncate">
                                                        {a.institution || a.account_type} · {a.currency}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 shrink-0">
                                                <span className="font-mono text-sm text-ocean-700 tabular-nums">{fmtMoney(a.balance, a.currency)}</span>
                                                {conflict && (
                                                    <Badge className="bg-coral/15 text-coral-dark text-[10px] uppercase tracking-wider">Different currency</Badge>
                                                )}
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {canonicalAcct && dups.size > 0 && conflicts.length === 0 && (
                        <div className="rounded-2xl bg-secondary/5 border border-secondary/30 px-4 py-3 text-sm text-secondary-dark" data-testid="merge-preview">
                            After merge: <span className="font-mono font-semibold">{canonicalAcct.name}</span> will hold{" "}
                            <span className="font-mono font-semibold">
                                {fmtMoney(
                                    Number(canonicalAcct.balance || 0) + Array.from(dups).reduce((acc, id) => acc + Number(accounts.find((x) => x.id === id)?.balance || 0), 0),
                                    canonicalAcct.currency,
                                )}
                            </span>
                            {" "}across all its transactions.
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => close(false)} disabled={busy}>Cancel</Button>
                    <Button
                        onClick={run}
                        disabled={!canonical || dups.size === 0 || conflicts.length > 0 || busy}
                        className="bg-ocean-700 text-white"
                        data-testid="merge-submit"
                    >
                        {busy
                            ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Merging…</>)
                            : (<><ArrowRightLeft className="h-4 w-4 mr-1.5" /> Merge {dups.size} account{dups.size === 1 ? "" : "s"}</>)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
